import React, { useState, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  LayoutDashboard,
  Inbox,
  TrendingUp,
  Megaphone,
  Briefcase,
  ShoppingBag,
  PackageCheck,
  FileText,
  Receipt,
  FileSignature,
  Landmark,
  BarChart3,
  Sparkles,
  Settings as SettingsIcon,
  DollarSign,
  Camera,
  Image as ImageIcon,
  Plus,
  X,
} from 'lucide-react';

export const FINANCE_NAV = [
  { group: 'Command', items: [
    { to: '/admin/finance', label: 'Dashboard', icon: LayoutDashboard, end: true },
    { to: '/admin/finance/inbox', label: 'Finance Inbox', icon: Inbox },
    { to: '/admin/finance/revenue', label: 'Revenue', icon: TrendingUp },
  ]},
  { group: 'Clients', items: [
    { to: '/admin/finance/advertisers', label: 'Advertisers', icon: Megaphone },
    { to: '/admin/finance/analytics-clients', label: 'Analytics clients', icon: Briefcase },
  ]},
  { group: 'Merchandise', items: [
    { to: '/admin/finance/merchandise', label: 'Merchandise', icon: ShoppingBag },
    { to: '/admin/finance/orders', label: 'Orders', icon: PackageCheck },
  ]},
  { group: 'Money', items: [
    { to: '/admin/finance/invoices', label: 'Invoices', icon: FileText },
    { to: '/admin/finance/expenses', label: 'Expenses', icon: Receipt },
    { to: '/admin/finance/contracts', label: 'Contracts', icon: FileSignature, badge: 'Soon' },
    { to: '/admin/finance/tax', label: 'Tax / VAT', icon: Landmark, badge: 'Soon' },
  ]},
  { group: 'Intelligence', items: [
    { to: '/admin/finance/reports', label: 'Reports', icon: BarChart3 },
    { to: '/admin/finance/forecast', label: 'Forecasting', icon: Sparkles, badge: 'Soon' },
    { to: '/admin/finance/settings', label: 'Settings', icon: SettingsIcon },
  ]},
];

export const FinanceLayout = ({ title, subtitle, actions, children }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-gradient-to-r from-teal-500 to-blue-600 text-white py-5 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              data-testid="back-to-admin"
              className="flex items-center gap-2 text-white/90 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" /> <span className="hidden sm:inline">Back</span>
            </button>
            <div>
              <div className="flex items-center gap-2">
                <DollarSign className="w-6 h-6" />
                <h1 className="text-xl sm:text-2xl font-black">Audiostride Finance</h1>
              </div>
              {title && <div className="text-teal-50 text-sm mt-0.5">{title}{subtitle ? ` · ${subtitle}` : ''}</div>}
            </div>
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      </header>

      {/* Mobile tab bar — horizontal scrolling chips for every finance section.
          Desktop shows the sidebar (below). Sticky under the header for
          always-available navigation. */}
      <div
        className="lg:hidden sticky top-0 z-20 bg-white border-b border-slate-200 shadow-sm"
        data-testid="finance-mobile-tabs"
      >
        <div className="max-w-7xl mx-auto overflow-x-auto no-scrollbar">
          <div className="flex items-center gap-1 px-3 py-2 whitespace-nowrap">
            {FINANCE_NAV.flatMap((group) => group.items).map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={`mobile-nav-${it.end ? 'dashboard' : it.to.split('/').pop()}`}
                className={({ isActive }) =>
                  `inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold transition flex-shrink-0 ${
                    isActive
                      ? 'bg-gradient-to-r from-teal-500 to-blue-600 text-white shadow-sm'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`
                }
              >
                <it.icon className="w-3.5 h-3.5" />
                <span>{it.label}</span>
                {it.badge && (
                  <span className="text-[9px] px-1 py-0.5 rounded-full bg-amber-100 text-amber-800 font-bold uppercase tracking-wider">
                    {it.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 flex gap-6">
        <aside className="w-60 flex-shrink-0 hidden lg:block">
          <nav className="bg-white rounded-2xl border border-slate-200 p-3 space-y-4 sticky top-4">
            {FINANCE_NAV.map((group) => (
              <div key={group.group}>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold px-3 pb-1">
                  {group.group}
                </div>
                <ul className="space-y-0.5">
                  {group.items.map((it) => (
                    <li key={it.to}>
                      <NavLink
                        to={it.to}
                        end={it.end}
                        data-testid={`nav-${it.end ? 'dashboard' : it.to.split('/').pop()}`}
                        className={({ isActive }) =>
                          `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-bold transition ${
                            isActive
                              ? 'bg-gradient-to-r from-teal-500 to-blue-600 text-white'
                              : 'text-slate-700 hover:bg-slate-100'
                          }`
                        }
                      >
                        <it.icon className="w-4 h-4 flex-shrink-0" />
                        <span className="flex-1 truncate">{it.label}</span>
                        {it.badge && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-bold uppercase tracking-wider">
                            {it.badge}
                          </span>
                        )}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </aside>
        <main className="flex-1 min-w-0 space-y-6">{children}</main>
      </div>
      <ReceiptFab />
    </div>
  );
};

// Floating Action Button — always-visible on every finance page so the founder
// can drop a receipt in 1 tap from anywhere in the Finance section. Optimized for
// thumb reach on iPhone (bottom-right, large hit area, sheet that slides up).
const ReceiptFab = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const cameraRef = useRef(null);
  const libraryRef = useRef(null);

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        sessionStorage.setItem('audiostride_inbox_pending_image', reader.result);
        sessionStorage.setItem('audiostride_inbox_pending_filename', file.name || `receipt-${Date.now()}.jpg`);
      } catch (_) { /* sessionStorage full — fall back to inbox without preload */ }
      setOpen(false);
      navigate('/admin/finance/inbox?source=fab');
    };
    reader.readAsDataURL(file);
  };

  return (
    <>
      {/* Backdrop + sheet */}
      {open && (
        <div
          className="fixed inset-0 z-[70] bg-black/40 flex items-end sm:items-center justify-center"
          onClick={() => setOpen(false)}
          data-testid="fab-sheet-backdrop"
        >
          <div
            className="bg-white w-full sm:w-96 rounded-t-3xl sm:rounded-3xl shadow-2xl p-5 pb-8 sm:pb-5 animate-in slide-in-from-bottom"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-black text-slate-900 text-lg">Add a receipt</div>
                <div className="text-xs text-slate-500">Snap it · we'll auto-parse it</div>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="fab-sheet-close"
                className="text-slate-500 hover:text-slate-900 p-1.5"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
              data-testid="fab-camera-input"
            />
            <input
              ref={libraryRef}
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
              data-testid="fab-library-input"
            />

            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => cameraRef.current?.click()}
                data-testid="fab-take-photo"
                className="flex flex-col items-center justify-center gap-2 py-7 rounded-2xl bg-gradient-to-br from-teal-500 to-blue-600 text-white font-bold shadow-lg active:scale-95 hover:opacity-95 transition"
              >
                <Camera className="w-8 h-8" />
                <span className="text-sm">Take photo</span>
              </button>
              <button
                type="button"
                onClick={() => libraryRef.current?.click()}
                data-testid="fab-from-library"
                className="flex flex-col items-center justify-center gap-2 py-7 rounded-2xl bg-slate-900 text-white font-bold shadow-lg active:scale-95 hover:bg-black transition"
              >
                <ImageIcon className="w-8 h-8" />
                <span className="text-sm">From library</span>
              </button>
            </div>

            <button
              type="button"
              onClick={() => { setOpen(false); navigate('/admin/finance/inbox'); }}
              data-testid="fab-open-inbox"
              className="mt-3 w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-slate-200 text-slate-700 font-bold text-sm hover:bg-slate-50 transition"
            >
              <Inbox className="w-4 h-4" /> Open Finance Inbox
            </button>
          </div>
        </div>
      )}

      {/* The FAB itself */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="finance-fab"
        aria-label="Add a receipt"
        className="fixed bottom-20 right-5 sm:bottom-7 sm:right-7 z-[60] flex items-center gap-2 pl-4 pr-5 py-4 rounded-full bg-gradient-to-r from-teal-500 to-blue-600 text-white font-black shadow-2xl active:scale-95 hover:opacity-95 transition"
      >
        <Plus className="w-5 h-5" />
        <span className="hidden sm:inline text-sm">Add receipt</span>
      </button>
    </>
  );
};

export const Kpi = ({ icon: Icon, label, value, sub, accent = 'from-slate-600 to-slate-800', onClick, testid, bodyContent }) => {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      onClick={onClick}
      data-testid={testid}
      className={`text-left bg-white rounded-2xl border border-slate-200 p-4 shadow-sm w-full ${onClick ? 'hover:border-blue-300 hover:shadow-md transition cursor-pointer' : ''}`}
    >
      <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-gradient-to-r ${accent} text-white text-[10px] font-bold uppercase tracking-wider mb-2`}>
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-2xl font-black text-slate-900">{value}</div>
      {sub && <div className="text-[11px] text-slate-600 mt-0.5">{sub}</div>}
      {bodyContent}
    </Tag>
  );
};

export const ComingSoon = ({ title, icon: Icon, what, needs }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-8" data-testid={`coming-soon-${title.replace(/\s+/g,'-').toLowerCase()}`}>
    <div className="flex items-start gap-4">
      <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
        <Icon className="w-7 h-7 text-white" />
      </div>
      <div className="flex-1">
        <h2 className="text-xl font-black text-slate-900">{title} · <span className="text-amber-600">Coming soon</span></h2>
        <p className="text-slate-600 mt-2">{what}</p>
        {needs && (
          <div className="mt-5 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="text-xs uppercase tracking-wider text-amber-700 font-bold mb-1">What I need from you to ship it</div>
            <div className="text-sm text-slate-700">{needs}</div>
          </div>
        )}
      </div>
    </div>
  </div>
);

export const Pill = ({ children, tone = 'slate' }) => {
  const cls = {
    emerald: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    blue: 'bg-blue-100 text-blue-800 border-blue-300',
    amber: 'bg-amber-100 text-amber-800 border-amber-300',
    rose: 'bg-rose-100 text-rose-800 border-rose-300',
    slate: 'bg-slate-100 text-slate-700 border-slate-300',
    violet: 'bg-violet-100 text-violet-800 border-violet-300',
  }[tone] || 'bg-slate-100 text-slate-700 border-slate-300';
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold border ${cls}`}>{children}</span>;
};

export const money = (n) =>
  (n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

export const Empty = ({ text, cta }) => (
  <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-600">
    <div>{text}</div>
    {cta && <div className="mt-4">{cta}</div>}
  </div>
);
